/**
 * API hooks для Demo Mode.
 */

import { useQuery, useMutation, type UseQueryResult } from '@tanstack/react-query';
import { client } from './client';

// ============================================================================
// Types
// ============================================================================

export interface DemoMessage {
  id: string;
  actorRole: 'client' | 'operator' | 'ai';
  text: string;
  createdAt: string;
  turnId: string | null;
}

export interface DemoProposal {
  id: string;
  text: string;
  status: 'thinking' | 'ready' | 'skipped' | 'matched';
  matchScore: number | null;
  shadowDecision: string | null;
  createdAt: string;
  sourceTurnId: string;
}

interface DemoSessionData {
  messages: DemoMessage[];
  proposals: DemoProposal[];
}

interface CreateDemoSessionResult {
  sessionId: string;
  maxMessages: number;
  greeting: string;
}

interface SendDemoMessageResult {
  messageId: string;
  turnId: string | null;
  remaining: number;
}

export interface OperatorReplyResult {
  matchScore: number;
  matchLabel: string;
  matchExplanation: string;
}

// ============================================================================
// Hooks
// ============================================================================

export function useCreateDemoSession() {
  return useMutation({
    mutationFn: async (): Promise<CreateDemoSessionResult> => {
      const { data } = await client.post<CreateDemoSessionResult>('/demo/sessions');
      return data;
    },
  });
}

export function useSendDemoMessage(sessionId: string | null) {
  return useMutation({
    mutationFn: async (text: string): Promise<SendDemoMessageResult> => {
      if (!sessionId) throw new Error('Session not initialized');
      const { data } = await client.post<SendDemoMessageResult>(
        `/demo/sessions/${sessionId}/messages`,
        { text }
      );
      return data;
    },
  });
}

export function useSendOperatorReply(sessionId: string | null) {
  return useMutation({
    mutationFn: async (text: string): Promise<OperatorReplyResult> => {
      if (!sessionId) throw new Error('Session not initialized');
      const { data } = await client.post<OperatorReplyResult>(
        `/demo/sessions/${sessionId}/operator-reply`,
        { text }
      );
      return data;
    },
  });
}

export function useDemoSession(
  sessionId: string | null,
): UseQueryResult<DemoSessionData, Error> {
  return useQuery({
    queryKey: ['demo-session', sessionId],
    queryFn: async () => {
      const { data } = await client.get<DemoSessionData>(`/demo/sessions/${sessionId}`);
      return data;
    },
    enabled: !!sessionId,
    refetchInterval: 2_000,
  });
}
